// src/components/FeedbacksView.tsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Stack,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  SelectChangeEvent,
  Avatar,
  LinearProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ReplyIcon from '@mui/icons-material/Reply';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InboxIcon from '@mui/icons-material/Inbox';
import { useTranslation } from 'react-i18next';
import { getFeedbacks, getFeedbackStatistics, deleteFeedback, searchFeedbacks } from '../services/feedbackService';
import { Feedback, FeedbackStatistics, ReportType } from '../types/feedback';
import RespondFeedbackDialog from './RespondFeedbackDialog';
import ViewFeedbackDialog from './ViewFeedbackDialog';

const REPORT_TYPE_CONFIG: Record<ReportType, { icon: React.ReactElement; color: string; bgColor: string }> = {
  bug: { icon: <BugReportIcon sx={{ fontSize: 18 }} />, color: '#dc2626', bgColor: '#fef2f2' },
  feature: { icon: <LightbulbIcon sx={{ fontSize: 18 }} />, color: '#d97706', bgColor: '#fef3c7' },
  complaint: { icon: <ReportProblemIcon sx={{ fontSize: 18 }} />, color: '#db2777', bgColor: '#fce7f3' },
  general: { icon: <InfoIcon sx={{ fontSize: 18 }} />, color: '#2563eb', bgColor: '#dbeafe' },
  other: { icon: <HelpOutlineIcon sx={{ fontSize: 18 }} />, color: '#64748b', bgColor: '#f1f5f9' },
};

const FeedbacksView: React.FC = () => {
  const { t } = useTranslation();
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [statistics, setStatistics] = useState<FeedbackStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'resolved'>('all');
  const [typeFilter, setTypeFilter] = useState<ReportType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [openRespondDialog, setOpenRespondDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState<Feedback | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const status = filter === 'all' ? undefined : filter;
      const reportType = typeFilter === 'all' ? undefined : typeFilter;

      if (searchQuery.trim()) {
        const searchResults = await searchFeedbacks(searchQuery, {
          is_resolved: status === 'pending' ? false : status === 'resolved' ? true : undefined,
          report_type: reportType,
          skip: page * rowsPerPage,
          limit: rowsPerPage,
        });
        setFeedbacks(searchResults || []);
        setTotal(searchResults.length);
      } else {
        const [feedbacksRes, statsRes] = await Promise.all([
          getFeedbacks(status, page * rowsPerPage, rowsPerPage, reportType),
          getFeedbackStatistics(),
        ]);
        setFeedbacks(feedbacksRes.feedbacks || []);
        setTotal(feedbacksRes.total || 0);
        setStatistics(statsRes);
      }
    } catch (err: any) {
      console.error('Failed to fetch feedbacks:', err);
      if (err.response?.status !== 404) {
        setError('Failed to fetch feedbacks');
      }
    } finally {
      setLoading(false);
    }
  }, [filter, typeFilter, searchQuery, page, rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(0);
  }, [filter, typeFilter, searchQuery]);

  const handleFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: 'all' | 'pending' | 'resolved' | null) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  const handleTypeFilterChange = (event: SelectChangeEvent<ReportType | 'all'>) => {
    setTypeFilter(event.target.value as ReportType | 'all');
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleRespond = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setOpenRespondDialog(true);
    setOpenViewDialog(false);
  };

  const handleRespondSuccess = () => {
    setOpenRespondDialog(false);
    setSelectedFeedback(null);
    fetchData();
  };

  const handleView = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setOpenViewDialog(true);
  };

  const handleDeleteClick = (feedback: Feedback) => {
    setFeedbackToDelete(feedback);
    setOpenDeleteDialog(true);
    setOpenViewDialog(false);
  };

  const handleDeleteConfirm = async () => {
    if (!feedbackToDelete) return;

    try {
      setDeleteLoading(true);
      await deleteFeedback(feedbackToDelete.id);
      setOpenDeleteDialog(false);
      setFeedbackToDelete(null);
      fetchData();
    } catch (err) {
      console.error('Failed to delete feedback:', err);
      setError(t('admin.feedbacks.deleteError'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const calculateResolutionRate = () => {
    if (statistics?.resolution_rate !== undefined) {
      return statistics.resolution_rate;
    }
    if (statistics && statistics.total > 0) {
      return Math.round((statistics.resolved / statistics.total) * 100);
    }
    return 0;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Il y a quelques minutes';
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading && feedbacks.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  const resolutionRate = calculateResolutionRate();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight={700} color="#1e293b">
          {t('admin.feedbacks.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Gérez les retours utilisateurs et les signalements
        </Typography>
      </Box>

      {/* Statistics */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      {t('admin.feedbacks.stats.total')}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                      {statistics.total}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5, bgcolor: '#dbeafe', borderRadius: 1 }}>
                    <InboxIcon sx={{ color: '#2563eb', fontSize: 24 }} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      {t('admin.feedbacks.stats.pending')}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="#d97706" sx={{ mt: 0.5 }}>
                      {statistics.pending}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5, bgcolor: '#fef3c7', borderRadius: 1 }}>
                    <ScheduleIcon sx={{ color: '#d97706', fontSize: 24 }} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      {t('admin.feedbacks.stats.resolved')}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="#16a34a" sx={{ mt: 0.5 }}>
                      {statistics.resolved}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5, bgcolor: '#dcfce7', borderRadius: 1 }}>
                    <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 24 }} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%', bgcolor: '#f0fdf4' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      {t('admin.feedbacks.resolutionRate')}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="#16a34a" sx={{ mt: 0.5 }}>
                      {resolutionRate}%
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5, bgcolor: '#dcfce7', borderRadius: 1 }}>
                    <TrendingUpIcon sx={{ color: '#16a34a', fontSize: 24 }} />
                  </Box>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={resolutionRate}
                  sx={{ mt: 1.5, height: 4, borderRadius: 0.5, bgcolor: '#dcfce7', '& .MuiLinearProgress-bar': { bgcolor: '#16a34a' } }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              placeholder={t('admin.feedbacks.search')}
              value={searchQuery}
              onChange={handleSearchChange}
              size="small"
              sx={{ minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#64748b' }} />
                  </InputAdornment>
                ),
              }}
            />

            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={handleFilterChange}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  border: '1px solid #e2e8f0',
                  '&.Mui-selected': { bgcolor: '#2563eb', color: 'white', '&:hover': { bgcolor: '#1d4ed8' } },
                },
              }}
            >
              <ToggleButton value="all">{t('admin.feedbacks.all')}</ToggleButton>
              <ToggleButton value="pending">{t('admin.feedbacks.pending')}</ToggleButton>
              <ToggleButton value="resolved">{t('admin.feedbacks.resolved')}</ToggleButton>
            </ToggleButtonGroup>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t('admin.feedbacks.filterByType')}</InputLabel>
              <Select
                value={typeFilter}
                label={t('admin.feedbacks.filterByType')}
                onChange={handleTypeFilterChange}
              >
                <MenuItem value="all">{t('admin.feedbacks.all')}</MenuItem>
                <MenuItem value="bug">{t('admin.feedbacks.reportTypes.bug')}</MenuItem>
                <MenuItem value="feature">{t('admin.feedbacks.reportTypes.feature')}</MenuItem>
                <MenuItem value="complaint">{t('admin.feedbacks.reportTypes.complaint')}</MenuItem>
                <MenuItem value="general">{t('admin.feedbacks.reportTypes.general')}</MenuItem>
                <MenuItem value="other">{t('admin.feedbacks.reportTypes.other')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {/* Feedbacks List */}
      {feedbacks.length > 0 ? (
        <>
          <Stack spacing={2}>
            {feedbacks.map((feedback) => {
              const typeConfig = REPORT_TYPE_CONFIG[feedback.report_type] || REPORT_TYPE_CONFIG.other;
              return (
                <Card key={feedback.id} sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', '&:hover': { borderColor: '#cbd5e1' } }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                      <Box sx={{ flexGrow: 1 }}>
                        {/* Header with Type and Status */}
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.5, bgcolor: typeConfig.bgColor, borderRadius: 0.5 }}>
                            {React.cloneElement(typeConfig.icon, { sx: { fontSize: 16, color: typeConfig.color } })}
                            <Typography variant="caption" fontWeight={600} sx={{ color: typeConfig.color, textTransform: 'uppercase' }}>
                              {t(`admin.feedbacks.reportTypes.${feedback.report_type}`)}
                            </Typography>
                          </Box>
                          {feedback.is_resolved ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, bgcolor: '#dcfce7', borderRadius: 0.5 }}>
                              <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                              <Typography variant="caption" fontWeight={600} color="#16a34a">
                                {t('admin.feedbacks.resolved')}
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, bgcolor: '#fef3c7', borderRadius: 0.5 }}>
                              <ScheduleIcon sx={{ fontSize: 16, color: '#d97706' }} />
                              <Typography variant="caption" fontWeight={600} color="#d97706">
                                {t('admin.feedbacks.pending')}
                              </Typography>
                            </Box>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                            {formatDate(feedback.created_at)}
                          </Typography>
                        </Stack>

                        {/* Title */}
                        <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                          {feedback.title}
                        </Typography>

                        {/* Description */}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {feedback.description}
                        </Typography>

                        {/* User Info */}
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 24, height: 24, bgcolor: '#2563eb', fontSize: 11 }}>
                              {feedback.user_name?.charAt(0).toUpperCase() || '?'}
                            </Avatar>
                            <Typography variant="body2" fontWeight={500} color="#1e293b">
                              {feedback.user_name}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {feedback.user_email}
                          </Typography>
                        </Stack>

                        {/* Admin Response Preview */}
                        {feedback.admin_response && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: '#f0fdf4', borderRadius: 1, borderLeft: '3px solid #16a34a' }}>
                            <Typography variant="caption" fontWeight={600} color="#16a34a" sx={{ display: 'block', mb: 0.5 }}>
                              {t('admin.feedbacks.response')}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {feedback.admin_response}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Actions */}
                      <Stack direction={{ xs: 'row', md: 'column' }} spacing={1} justifyContent="flex-start">
                        <Tooltip title={t('admin.feedbacks.viewDetails')}>
                          <IconButton
                            size="small"
                            onClick={() => handleView(feedback)}
                            sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}
                          >
                            <VisibilityIcon sx={{ fontSize: 18, color: '#64748b' }} />
                          </IconButton>
                        </Tooltip>
                        {!feedback.is_resolved && (
                          <Tooltip title={t('admin.feedbacks.respond')}>
                            <IconButton
                              size="small"
                              onClick={() => handleRespond(feedback)}
                              sx={{ bgcolor: '#dbeafe', '&:hover': { bgcolor: '#bfdbfe' } }}
                            >
                              <ReplyIcon sx={{ fontSize: 18, color: '#2563eb' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={t('admin.feedbacks.delete')}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(feedback)}
                            sx={{ bgcolor: '#fef2f2', '&:hover': { bgcolor: '#fee2e2' } }}
                          >
                            <DeleteIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>

          {/* Pagination */}
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', mt: 2 }}>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage={t('common.rowsPerPage')}
            />
          </Card>
        </>
      ) : (
        <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
          <CardContent sx={{ py: 8, textAlign: 'center' }}>
            <Box sx={{ width: 64, height: 64, bgcolor: '#f1f5f9', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <InboxIcon sx={{ fontSize: 32, color: '#64748b' }} />
            </Box>
            <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
              {t('admin.feedbacks.noFeedbacks')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Aucun feedback ne correspond à vos critères de recherche
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* View Dialog */}
      {selectedFeedback && openViewDialog && (
        <ViewFeedbackDialog
          open={openViewDialog}
          onClose={() => {
            setOpenViewDialog(false);
            setSelectedFeedback(null);
          }}
          feedback={selectedFeedback}
          onRespond={handleRespond}
          onDelete={handleDeleteClick}
        />
      )}

      {/* Respond Dialog */}
      {selectedFeedback && openRespondDialog && (
        <RespondFeedbackDialog
          open={openRespondDialog}
          onClose={() => {
            setOpenRespondDialog(false);
            setSelectedFeedback(null);
          }}
          feedback={selectedFeedback}
          onSuccess={handleRespondSuccess}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>{t('admin.feedbacks.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('admin.feedbacks.deleteConfirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDeleteDialog(false)} disabled={deleteLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeedbacksView;
